import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Building2, Trash2, Star, Edit2, Upload, X } from "lucide-react";

const OrganizationsPage = () => {
  const { organizations, fetchOrganizations, currentOrg, setCurrentOrg } = useOrg();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [form, setForm] = useState({
    name: "", address_street: "", address_city: "", address_state: "",
    address_zip: "", address_country: "", phone: "", email: "", website: "",
    industry_type: "", fein_number: "", default_currency: "USD", invoice_prefix: "INV-",
    custom_fields: []
  });

  const resetForm = () => {
    setForm({
      name: "", address_street: "", address_city: "", address_state: "",
      address_zip: "", address_country: "", phone: "", email: "", website: "",
      industry_type: "", fein_number: "", default_currency: "USD", invoice_prefix: "INV-",
      custom_fields: []
    });
    setEditingOrg(null);
    setNewFieldLabel("");
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    const cf = Array.isArray(org.custom_fields) ? org.custom_fields : [];
    setForm({
      name: org.name || "", address_street: org.address_street || "",
      address_city: org.address_city || "", address_state: org.address_state || "",
      address_zip: org.address_zip || "", address_country: org.address_country || "",
      phone: org.phone || "", email: org.email || "", website: org.website || "",
      industry_type: org.industry_type || "", fein_number: org.fein_number || "",
      default_currency: org.default_currency || "USD", invoice_prefix: org.invoice_prefix || "INV-",
      custom_fields: cf.map(f => ({ label: f.label || "", value: f.value || "", show_on_invoice: f.show_on_invoice !== false }))
    });
    setDialogOpen(true);
  };

  const addCustomField = () => {
    const label = newFieldLabel.trim();
    if (!label) return;
    if (form.custom_fields.some(f => f.label.toLowerCase() === label.toLowerCase())) {
      toast.error("Field already exists");
      return;
    }
    setForm({ ...form, custom_fields: [...form.custom_fields, { label, value: "", show_on_invoice: true }] });
    setNewFieldLabel("");
  };

  const updateCustomField = (index, key, value) => {
    const updated = [...form.custom_fields];
    updated[index] = { ...updated[index], [key]: value };
    setForm({ ...form, custom_fields: updated });
  };

  const removeCustomField = (index) => {
    setForm({ ...form, custom_fields: form.custom_fields.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingOrg) {
        await axios.put(`${API}/organizations/${editingOrg.org_id}`, form, { withCredentials: true });
        toast.success("Organization updated");
      } else {
        await axios.post(`${API}/organizations`, form, { withCredentials: true });
        toast.success("Organization created");
      }
      await fetchOrganizations();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (orgId) => {
    if (!confirm("Are you sure you want to delete this organization?")) return;
    try {
      await axios.delete(`${API}/organizations/${orgId}`, { withCredentials: true });
      toast.success("Organization deleted");
      await fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handleSetDefault = async (orgId) => {
    try {
      await axios.post(`${API}/organizations/${orgId}/set-default`, {}, { withCredentials: true });
      toast.success("Default organization updated");
      await fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const handleLogoUpload = async (orgId, file) => {
    if (!file) return;
    setUploadingLogo(orgId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/organizations/${orgId}/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Logo uploaded");
      await fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(null);
    }
  };

  const handleDeleteLogo = async (orgId) => {
    try {
      await axios.delete(`${API}/organizations/${orgId}/logo`, { withCredentials: true });
      toast.success("Logo removed");
      await fetchOrganizations();
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  return (
    <div className="space-y-6" data-testid="organizations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900">Organizations</h1>
          <p className="text-muted-foreground text-sm">Manage your business organizations</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-org-btn">
              <Plus className="w-4 h-4" /> Add Organization
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingOrg ? "Edit Organization" : "Create Organization"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Organization Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="org-name-input" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>Street Address</Label>
                  <Input value={form.address_street} onChange={(e) => setForm({...form, address_street: e.target.value})} />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={form.address_city} onChange={(e) => setForm({...form, address_city: e.target.value})} />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={form.address_state} onChange={(e) => setForm({...form, address_state: e.target.value})} />
                </div>
                <div>
                  <Label>ZIP Code</Label>
                  <Input value={form.address_zip} onChange={(e) => setForm({...form, address_zip: e.target.value})} />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input value={form.address_country} onChange={(e) => setForm({...form, address_country: e.target.value})} />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} />
                </div>
                <div>
                  <Label>Industry</Label>
                  <Input value={form.industry_type} onChange={(e) => setForm({...form, industry_type: e.target.value})} placeholder="e.g., Retail, Services" />
                </div>
                <div>
                  <Label>FEIN Number</Label>
                  <Input value={form.fein_number} onChange={(e) => setForm({...form, fein_number: e.target.value})} />
                </div>
                <div>
                  <Label>Default Currency</Label>
                  <Input value={form.default_currency} onChange={(e) => setForm({...form, default_currency: e.target.value})} />
                </div>
                <div>
                  <Label>Invoice Prefix</Label>
                  <Input value={form.invoice_prefix} onChange={(e) => setForm({...form, invoice_prefix: e.target.value})} placeholder="INV-" />
                </div>
              </div>

              {/* Custom Fields */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-zinc-700 mb-3">Custom Fields</h3>
                <p className="text-xs text-muted-foreground mb-3">Add custom fields (e.g., License Number, Tax ID). Toggle "Show on Invoice" to include on printed invoices.</p>

                {form.custom_fields.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {form.custom_fields.map((field, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-zinc-50 border border-zinc-100" data-testid={`custom-field-${i}`}>
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">{field.label}</Label>
                            <Input
                              value={field.value}
                              onChange={(e) => updateCustomField(i, 'value', e.target.value)}
                              placeholder={`Enter ${field.label}`}
                              data-testid={`custom-field-value-${i}`}
                            />
                          </div>
                          <div className="flex items-end gap-3">
                            <div className="flex items-center gap-2 pb-2">
                              <Checkbox
                                id={`cf-invoice-${i}`}
                                checked={field.show_on_invoice}
                                onCheckedChange={(checked) => updateCustomField(i, 'show_on_invoice', checked)}
                                data-testid={`custom-field-invoice-${i}`}
                              />
                              <Label htmlFor={`cf-invoice-${i}`} className="text-xs font-normal cursor-pointer whitespace-nowrap">
                                Show on Invoice
                              </Label>
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeCustomField(i)} className="text-destructive hover:text-destructive mb-1">
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    value={newFieldLabel}
                    onChange={(e) => setNewFieldLabel(e.target.value)}
                    placeholder="Field label (e.g., Tobacco License)"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomField(); } }}
                    data-testid="custom-field-label-input"
                  />
                  <Button type="button" variant="outline" onClick={addCustomField} data-testid="add-custom-field-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={loading} data-testid="org-submit-btn">
                  {loading ? "Saving..." : (editingOrg ? "Update" : "Create")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Organizations List */}
      <div className="grid gap-4">
        {organizations.length === 0 ? (
          <div className="text-center py-16 bg-white border border-zinc-200">
            <Building2 className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No organizations yet</p>
            <Button onClick={() => setDialogOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" /> Create your first organization
            </Button>
          </div>
        ) : (
          organizations.map((org) => (
            <div key={org.org_id} className={`bg-white border p-6 ${org.is_default ? 'border-primary' : 'border-zinc-200'}`} data-testid={`org-card-${org.org_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="relative group">
                    {org.logo_base64 ? (
                      <div className="w-16 h-16 border border-zinc-200 flex items-center justify-center overflow-hidden">
                        <img src={`data:image/png;base64,${org.logo_base64}`} alt={org.name} className="w-full h-full object-contain" />
                        <button onClick={() => handleDeleteLogo(org.org_id)} className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="w-16 h-16 border-2 border-dashed border-zinc-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(org.org_id, e.target.files?.[0])} />
                        {uploadingLogo === org.org_id ? (
                          <span className="text-xs text-muted-foreground">...</span>
                        ) : (
                          <><Upload className="w-4 h-4 text-muted-foreground" /><span className="text-[10px] text-muted-foreground mt-1">Logo</span></>
                        )}
                      </label>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-outfit font-medium text-lg">{org.name}</h3>
                      {org.is_default && <span className="bg-primary/10 text-primary text-xs px-2 py-0.5">Default</span>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {[org.address_city, org.address_state, org.address_country].filter(Boolean).join(", ") || "No address"}
                    </p>
                    {org.email && <p className="text-sm text-muted-foreground">{org.email}</p>}
                    {/* Show custom fields */}
                    {Array.isArray(org.custom_fields) && org.custom_fields.filter(f => f.value).length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-2">
                        {org.custom_fields.filter(f => f.value).map((f, i) => (
                          <span key={i} className="text-xs text-muted-foreground bg-zinc-50 px-2 py-1 border border-zinc-100">
                            {f.label}: {f.value}
                            {f.show_on_invoice && <span className="ml-1 text-primary text-[10px]">(invoice)</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!org.is_default && (
                    <Button variant="ghost" size="sm" onClick={() => handleSetDefault(org.org_id)} className="gap-1">
                      <Star className="w-4 h-4" /> Set Default
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(org)} data-testid={`edit-org-${org.org_id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(org.org_id)} className="text-destructive hover:text-destructive" data-testid={`delete-org-${org.org_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrganizationsPage;
